import { Injectable } from "@angular/core";
import { environment } from "@environments/environment";
import { NotaFiscalServico } from "@interfaces/nfse";
import { NfsePayload } from "@interfaces/nfse-payload";
import {
  NfseRascunho,
  NfseRascunhoResumo
} from "@interfaces/nfse-rascunho";
import { NfseReemissao } from "@interfaces/nfse-reemissao";
import { HttpService } from "./http.service";

@Injectable({
  providedIn: "root"
})
export class NfseService {
  url = `${environment.api.mv.nf}/nfse`;

  constructor(private readonly httpService: HttpService) {}

  private normalizeIssEnumValue(
    value: unknown,
    fallback: number,
    validValues: readonly number[]
  ): number {
    const valueAsString =
      typeof value === "string" || typeof value === "number"
        ? String(value).trim()
        : "";

    const numericValue =
      typeof value === "number" ? value : Number.parseInt(valueAsString, 10);

    if (!Number.isInteger(numericValue)) {
      return fallback;
    }

    return validValues.includes(numericValue) ? numericValue : fallback;
  }

  private normalizeIbgeCode(value: unknown): string {
    if (typeof value !== "string" && typeof value !== "number") {
      return "";
    }

    return String(value).replaceAll(/\D/g, "");
  }

  private normalizeServiceCode(value: unknown): string {
    if (typeof value !== "string" && typeof value !== "number") {
      return "";
    }

    const serviceCode = String(value).trim();
    const digits = serviceCode.replaceAll(/\D/g, "");

    // The national tax-reform layout expects cTribNac with exactly six
    // digits. Preserve shorter legacy codes because other municipal layouts
    // still accept values such as "10.09".
    return digits.length === 6 ? digits : serviceCode;
  }

  private normalizeCnae(value: unknown): string {
    if (typeof value !== "string" && typeof value !== "number") {
      return "";
    }

    return String(value).replaceAll(/\D/g, "").trim();
  }

  private normalizeCodigoNbs(value: unknown): string {
    if (typeof value !== "string" && typeof value !== "number") {
      return "";
    }

    return String(value).replaceAll(/\D/g, "").trim();
  }

  private normalizeSerieRps(payload: NfsePayload): string {
    const serie = (payload.rps?.serie ?? payload.serie ?? "").toString().trim();
    return serie || "1";
  }

  issue(payload: NfsePayload) {
    const normalizedSerieRps = this.normalizeSerieRps(payload);
    const cidadePrestacaoCode = this.normalizeIbgeCode(
      payload.cidadePrestacao?.codigo
    );
    const tomadorCodigoCidade = this.normalizeIbgeCode(
      payload.tomador?.endereco?.codigoCidade
    );
    const normalizedTomador = payload.tomador
      ? {
          ...payload.tomador,
          endereco: payload.tomador.endereco
            ? {
                ...payload.tomador.endereco,
                codigoCidade:
                  tomadorCodigoCidade || payload.tomador.endereco.codigoCidade
              }
            : payload.tomador.endereco
        }
      : payload.tomador;
    const normalizedServices = payload.servico?.map((service) => ({
      ...service,
      codigo: this.normalizeServiceCode(service?.codigo),
      cnae: this.normalizeCnae(service?.cnae),
      codigoNbs: this.normalizeCodigoNbs(service?.codigoNbs),
      ibscbs: {
        ...service.ibscbs,
        finalidadeNFSe: service.ibscbs?.finalidadeNFSe ?? 0
      },
      iss: {
        ...service.iss,
        tipoTributacao: this.normalizeIssEnumValue(
          service.iss?.tipoTributacao,
          6,
          [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]
        ),
        exigibilidade: this.normalizeIssEnumValue(
          service.iss?.exigibilidade,
          1,
          [1, 2, 3, 4, 5, 6, 7]
        )
      }
    }));

    const requestPayload = {
      ...payload,
      serie: normalizedSerieRps,
      rps: {
        ...payload.rps,
        serie: normalizedSerieRps
      },
      cidadePrestacao: {
        ...payload.cidadePrestacao,
        codigo: cidadePrestacaoCode || payload.cidadePrestacao?.codigo
      },
      tomador: normalizedTomador,
      servico: normalizedServices
    };

    return this.httpService.post(this.url, requestPayload);
  }

  fetchNfes(
    cnpj: string,
    dataInicial: string | null = null,
    dataFinal: string | null = null
  ) {
    const params: string[] = [];
    if (dataInicial) params.push(`dataInicial=${dataInicial}`);
    if (dataFinal) params.push(`dataFinal=${dataFinal}`);

    const queryString = params.length ? `?${params.join("&")}` : "";

    return this.httpService.get<{
      hashProximaPagina: string;
      notas: NotaFiscalServico[];
    }>(`${this.url}/${cnpj}${queryString}`);
  }

  /**
   * Dados de uma NFS-e rejeitada para reabrir o formulário preenchido
   * ("Corrigir e reemitir"). `id` é o id local da nota — na listagem do
   * PlugNotas ele vem em `idIntegracao` (o `id` da listagem é o do PlugNotas).
   */
  fetchReemissao(id: string) {
    return this.httpService.get<NfseReemissao>(`${this.url}/${id}/reemissao`);
  }

  /**
   * Rascunho = emissão interrompida no "Voltar". Vai sem normalização e sem
   * validação fiscal de propósito: o formulário pode estar incompleto; quem
   * valida é a emissão.
   */
  saveRascunho(payload: NfsePayload, id?: string) {
    return this.httpService.post<NfseRascunhoResumo>(`${this.url}/rascunho`, {
      id,
      payload
    });
  }

  fetchRascunhos() {
    return this.httpService.get<NfseRascunhoResumo[]>(`${this.url}/rascunho`);
  }

  fetchRascunho(id: string) {
    return this.httpService.get<NfseRascunho>(`${this.url}/rascunho/${id}`);
  }

  deleteRascunho(id: string) {
    return this.httpService.del<{ id: string }>(`${this.url}/rascunho/${id}`);
  }

  downloadNFSeFile(idNota: string, type: "pdf" | "xml") {
    return this.httpService.getBlob(`${this.url}/download/${type}/${idNota}`);
  }

  getExternalNfseDownloadLink(idNota: string, type: "pdf" | "xml") {
    return this.httpService.get<{ url: string }>(
      `${this.url}/download/${type}/${idNota}/external-link`
    );
  }

  requestNfseCancellation(idNota: string, motivo: string) {
    return this.httpService.post(`${this.url}/cancelar/${idNota}`, {
      motivo
    });
  }
}
